// src/admin/pages/IntegrationsAdmin.tsx
//
// Pannello admin per gestire integrazioni esterne. Per ora contiene solo
// Google Calendar (mono-direzionale: le prenotazioni del sito appaiono
// come eventi sul Google Calendar del titolare; cancellandole vengono
// rimosse). Il flow OAuth è gestito lato BE: qui apriamo il link init
// in una nuova tab e mostriamo status/feedback.

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/insolita';

interface GoogleStatus {
  configured: boolean;
  connected: boolean;
  googleEmail?: string;
  calendarId?: string;
  connectedAt?: string;
}

const IntegrationsAdmin: React.FC = () => {
  const [status, setStatus] = useState<GoogleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await adminApi.googleCalendarStatus();
      setStatus(s);
    } finally {
      setLoading(false);
    }
  }, []);

  // Leggi il feedback OAuth dopo il redirect di ritorno.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const gState = p.get('google');
    if (gState === 'connected') {
      setBanner({ kind: 'ok', text: `Google Calendar collegato correttamente${p.get('email') ? ` (${p.get('email')})` : ''}.` });
      navigate('/admin/integrations', { replace: true });
    } else if (gState === 'error') {
      setBanner({ kind: 'err', text: `Errore nel collegamento a Google: ${p.get('msg') || 'sconosciuto'}` });
      navigate('/admin/integrations', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const connect = async () => {
    // Step 1: chiediamo al BE l'URL di consent Google (chiamata autenticata
    // con JWT admin via axios). Non possiamo aprire /oauth/init direttamente
    // in nuova tab: sarebbe una navigation full-page, il browser non
    // manderebbe l'header Authorization e otterremmo 401.
    try {
      const url = await adminApi.googleCalendarAuthUrl();
      // Step 2: apriamo l'URL di Google (pubblico, no auth) in nuova tab.
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      setBanner({ kind: 'err', text: 'Impossibile aprire il consent Google: ' + (e?.response?.data?.message || e.message) });
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Scollegare Google Calendar? Le prenotazioni future non verranno più sincronizzate. Gli eventi già creati NON verranno rimossi da Google.')) return;
    await adminApi.googleCalendarDisconnect();
    setBanner({ kind: 'ok', text: 'Google Calendar scollegato.' });
    refresh();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Integrazioni</h1>
        <p className="text-neutral-600 mt-1 text-sm">Collega servizi esterni al gestionale.</p>
      </div>

      {banner && (
        <div className={`p-4 rounded-lg text-sm ${banner.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {banner.text}
        </div>
      )}

      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <img src="https://www.gstatic.com/images/branding/product/2x/calendar_48dp.png" alt="Google Calendar" className="w-10 h-10" />
              <h2 className="text-lg font-semibold">Google Calendar</h2>
            </div>
            <p className="text-sm text-neutral-600 mt-3">
              Quando ricevi una nuova prenotazione, la aggiungiamo automaticamente al tuo Google Calendar
              come evento con nome cliente, tour e numero di partecipanti. Se la prenotazione viene
              cancellata (dal cliente o da te), l'evento viene rimosso da Google. La sincronizzazione è
              solo dal sito verso Google: le modifiche fatte su Google Calendar NON tornano al sito.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-neutral-500 mt-6">Caricamento…</p>
        ) : !status?.configured ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mt-6">
            Integrazione non ancora configurata lato server. Contatta lo sviluppatore.
          </p>
        ) : status.connected ? (
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-emerald-800 font-medium">Collegato</span>
              {status.googleEmail && <span className="text-neutral-600">come <b>{status.googleEmail}</b></span>}
            </div>
            {status.connectedAt && (
              <p className="text-xs text-neutral-500">
                Collegato il {new Date(status.connectedAt).toLocaleString('it-IT')}
              </p>
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={refresh} className="px-4 py-2 border border-neutral-300 rounded text-sm hover:bg-neutral-50">
                Aggiorna stato
              </button>
              <button onClick={disconnect} className="px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                Scollega
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-neutral-400" />
              <span className="text-neutral-600 font-medium">Non collegato</span>
            </div>
            <p className="text-sm text-neutral-600">
              Clicca il pulsante qui sotto per collegare il tuo account Google. Ti verrà chiesto di
              autorizzare l'accesso al Calendar; noi useremo quel permesso solo per creare/modificare
              gli eventi delle prenotazioni.
            </p>
            <button onClick={connect} className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B0B0C] text-white rounded font-medium hover:bg-neutral-800">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5C29.6 34.6 26.9 36 24 36c-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.2-2.1 4.1-3.9 5.5l6.5 5.5C42.4 36 44 30 44 24c0-1.3-.1-2.7-.4-3.5z"/></svg>
              Collega Google Calendar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsAdmin;
